/**
 * Cookie consent (THEME.md §7.25; D-136, T-219): the element of `sections/okt-consent.liquid`, and the handler of
 * `#cookie-preferences` links on every page.
 *
 * - The banner is Horizon's dialog (it extends `DialogComponent`): the pop-up, the drawers and a dimmed sheet open
 *   modal, as Horizon's (Escape, a click outside, the scroll lock and the closing animation are Horizon's); the bottom
 *   sheet opens with show(), so the page stays usable. The preferences are Horizon's `dialog-component`, always modal.
 * - When: after the load event, once the browser is idle, it loads Shopify's Customer Privacy API
 *   (`consent-tracking-api` 0.2, or the version an app took first, T-265) and opens only while currentVisitorConsent()
 *   holds no answer, and then where shouldShowBanner() says so (Show to "Where consent is required": a consent region)
 *   or anywhere (Show to "Every visitor"). shouldShowBanner() alone doesn't know about an answer (T-264). Without the
 *   API nothing shows, and Shopify keeps non-essential processing off where consent is required. The focus moves to
 *   the title, unless the visitor is typing in a field.
 * - Recording, only on a visitor's click: Accept all and Reject all call setTrackingConsent with analytics, marketing
 *   and preferences all true or all false; Save records the ticked ones (none ticked: as Reject all); sale_of_data is
 *   never set. Shopify then dispatches `visitorConsentCollected`, which the Clarity loader and the overlays hear. The
 *   answer is also kept for the next page's readers, as the API's 0.2 keeps it (keepAnswer, T-265).
 * - Closing without a choice (the X with Close button on, Escape there; Escape or a click outside where the page is
 *   dimmed) records nothing: the banner returns on the next page.
 * - Manage preferences, and any link to #cookie-preferences in any region: the preferences, ticked as the visitor
 *   answered; closed without a choice, the banner comes back if it was open. With Show to visitors off, the links
 *   open Shopify's banner's preferences (`window.privacyBanner.showPreferences()`).
 * - The overlays' order (THEME.md §7.24): the store suggestion and the newsletter pop-up wait while an element marked
 *   `data-okt-consent-banner` shows: both dialogs, and the zero-size marker until the banner opens or isn't needed.
 * - Theme editor: the banner while the section or one of its blocks is selected, the preferences while theirs is;
 *   nothing is recorded.
 */
import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { onAnimationEnd, requestIdleCallback } from '@theme/utilities';

const HASH = '#cookie-preferences';
const LINKS = `a[href$="${HASH}"]`;
const PURPOSES = ['analytics', 'marketing', 'preferences'];
const DESKTOP = window.matchMedia('(min-width: 750px)');
const EDITABLE =
  'input:not([type="hidden"], [type="checkbox"], [type="radio"], [type="button"], [type="submit"]), textarea, select, [contenteditable]:not([contenteditable="false"])';

/** What the theme editor shows of each section, so a section rendered again after a change shows it again */
const shownInEditor = new Map();

/** @type {Promise<any> | undefined} */
let privacy;

/**
 * Shopify's Customer Privacy API, loaded once. Two things kept the banner shut (T-265):
 * - Shopify's loader takes one version of it per page and refuses any other ("consent-tracking-api@0.2 has already
 *   been loaded at version 0.1"): Judge.me's reviews ask for 0.1 on product pages, mostly before the load event. Then
 *   the page's version is used, once its script has loaded; it has everything used here.
 * - The loader calls back on the script's load event, and Safari can run the script a moment later: the API is awaited.
 * @returns {Promise<any>}
 */
function privacyApi() {
  privacy ??= new Promise((resolve, reject) => {
    const shopify = /** @type {any} */ (window).Shopify;
    if (typeof shopify?.loadFeatures !== 'function') {
      reject(new Error('Shopify.loadFeatures is missing'));
      return;
    }

    const ready = (tries = 50) => {
      const api = /** @type {any} */ (window).Shopify?.customerPrivacy;
      if (api) resolve(api);
      else if (tries > 0) setTimeout(() => ready(tries - 1), 100);
      else reject(new Error('Shopify.customerPrivacy is missing'));
    };

    const load = (/** @type {string} */ version) =>
      shopify.loadFeatures([{ name: 'consent-tracking-api', version }], (/** @type {unknown} */ error) => {
        const taken = /loaded at version ([\d.]+)/.exec(String(error))?.[1];
        if (taken && taken !== version) load(taken);
        else if (error) reject(error);
        else ready();
      });

    load('0.2');
  });

  return privacy;
}

/**
 * Keeps a recorded answer where the next page's readers look for it, as the API's 0.2 does (T-265). Shopify tells each
 * page the visitor's answer in its navigation's Server-Timing (`_cmp`), and its readers (the API, the pixels) fall back
 * to the last one in sessionStorage `consentHeader`. Safari hides that timing after a cross-origin redirect, and Shop
 * sends the session's second page through shop.app (`/accounts/sur`). 0.2 stores the new answer there as it records
 * it; 0.1 (Judge.me's, on product pages) doesn't, so after an answer there the next page read "no answer" and the banner
 * came back.
 * @param {any} result setTrackingConsent's response
 */
function keepAnswer(result) {
  const answer = result?.data?.consentManagement?.cookies?.trackingConsentCookie;
  if (typeof answer !== 'string' || !answer) return;

  try {
    sessionStorage.setItem('consentHeader', decodeURIComponent(answer));
  } catch {
    // Storage blocked: the readers go by the page's Server-Timing alone
  }
}

/** The page's load event */
const loaded = new Promise((resolve) => {
  if (document.readyState === 'complete') resolve(undefined);
  else window.addEventListener('load', () => resolve(undefined), { once: true });
});

/**
 * The pop-up and the drawers open modal, as a sheet does with Dim page behind sheet
 * @param {HTMLDialogElement} dialog
 * @param {boolean} dim
 */
function isModal(dialog, dim) {
  const shown = DESKTOP.matches ? dialog.dataset.desktop : dialog.dataset.phone;
  return shown !== 'sheet' || dim;
}

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 *
 * @extends {DialogComponent}
 */
class OktConsent extends DialogComponent {
  /** @type {AbortController | undefined} */
  #abortController;

  /** Manage preferences or a link opened the preferences over the banner: closed without a choice, it comes back */
  #bannerWaits = false;

  /** @type {HTMLElement | null} A field the visitor was typing in as the sheet opened, which keeps the focus */
  #typing = null;

  get #designMode() {
    return this.hasAttribute('data-design-mode');
  }

  /** @returns {any} The preferences: Horizon's `dialog-component` */
  get #panel() {
    return this.querySelector('.okt-consent-preferences');
  }

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener(DialogOpenEvent.eventName, this.#onOpen, { signal });
    this.#panel?.addEventListener(DialogOpenEvent.eventName, this.#onPanelOpen, { signal });
    this.#panel?.addEventListener(DialogCloseEvent.eventName, this.#onPanelClose, { signal });
    this.refs.dialog.addEventListener('keydown', this.#onKeyDown, { signal });

    if (this.#designMode) {
      document.addEventListener('shopify:section:select', this.#onSectionSelect, { signal });
      document.addEventListener('shopify:section:deselect', this.#onSectionDeselect, { signal });
      document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });

      const shown = shownInEditor.get(this.dataset.sectionId);
      if (shown === 'preferences') this.showPreferences();
      else if (shown === 'banner') this.open();
      return;
    }

    if (!this.hasAttribute('data-enabled')) return;

    window.addEventListener('pageshow', this.#onPageShow, { signal });
    this.#start(signal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /** @param {AbortSignal} signal */
  async #start(signal) {
    try {
      await loaded;
      await new Promise((resolve) => requestIdleCallback(() => resolve(undefined)));
      const api = await privacyApi();
      if (signal.aborted) return;

      if (this.#isNeeded(api)) {
        this.open();
        return;
      }
    } catch {
      // Shopify's API didn't load: nothing shows, and where consent is required Shopify keeps tracking off
    }

    this.#settle();
  }

  /** @param {any} api */
  #isNeeded(api) {
    // A visitor who has answered (a yes or a no for any purpose) never sees the banner again. shouldShowBanner() only
    // says the store wants a banner in the visitor's region: with Shopify's own banner removed it stays true after an
    // answer (seen live, 22 Oct), so on its own it reopened the banner on every page.
    const consent = api.currentVisitorConsent?.() ?? {};
    if (PURPOSES.some((purpose) => consent[purpose] === 'yes' || consent[purpose] === 'no')) return false;
    if (this.dataset.showTo === 'all') return true;

    return api.shouldShowBanner?.() === true;
  }

  /** The decision is made: the marker no longer holds the other overlays back */
  #settle() {
    this.querySelector('[data-okt-consent-pending]')?.remove();
  }

  /** Opens the banner: modal as the pop-up and the drawers, else the sheet that leaves the page usable */
  open() {
    const { dialog } = this.refs;
    if (dialog.open) return;

    // The preferences are open (a link to them came first): the banner waits for them
    if (this.#panel?.refs?.dialog?.open) {
      this.#bannerWaits = true;
      return;
    }

    const active = document.activeElement;
    this.#typing = active instanceof HTMLElement && active.matches(EDITABLE) ? active : null;

    if (isModal(dialog, this.hasAttribute('data-dim'))) {
      this.showDialog();
      return;
    }

    dialog.show();
    this.dispatchEvent(new DialogOpenEvent());
  }

  /** Closes the banner: Horizon's close for a modal one, else the same slide out */
  async #close() {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    if (dialog.matches(':modal')) {
      await this.closeDialog();
      return;
    }

    dialog.classList.add('dialog-closing');
    await onAnimationEnd(dialog, undefined, { subtree: false });
    dialog.close();
    dialog.classList.remove('dialog-closing');
    this.dispatchEvent(new DialogCloseEvent());
  }

  /** The X: closes without a choice; nothing is recorded */
  dismiss = () => {
    this.#close();
  };

  accept = () => this.#record(true);

  reject = () => this.#record(false);

  /** Save my choices: the ticked purposes */
  save = () => {
    /** @type {Record<string, boolean>} */
    const consent = {};
    for (const purpose of PURPOSES) {
      const input = this.#panel?.querySelector(`[data-okt-consent-purpose="${purpose}"]`);
      consent[purpose] = input instanceof HTMLInputElement && input.checked;
    }
    return this.#record(consent);
  };

  /**
   * Records the visitor's choice in Shopify's Customer Privacy API and closes the banner and the preferences
   * @param {boolean | Record<string, boolean>} choice
   */
  async #record(choice) {
    const consent =
      typeof choice === 'boolean' ? Object.fromEntries(PURPOSES.map((purpose) => [purpose, choice])) : choice;
    this.#bannerWaits = false;

    if (!this.#designMode) {
      privacyApi()
        .then((api) =>
          api.setTrackingConsent(consent, (/** @type {unknown} */ error, /** @type {any} */ result) => {
            if (!error) keepAnswer(result);
          })
        )
        .catch(() => {
          // Without the API nothing is recorded, and nothing tracks where consent is required
        });
    }

    const panel = this.#panel;
    await Promise.all([this.#close(), panel?.refs?.dialog?.open ? panel.closeDialog() : undefined]);
  }

  /** Manage preferences, and links to #cookie-preferences: the preferences, ticked as the visitor answered */
  showPreferences = async () => {
    const panel = this.#panel;
    if (!panel || panel.refs?.dialog?.open) return;

    if (this.refs.dialog.open) {
      this.#bannerWaits = true;
      await this.#close();
    }

    /** @type {Record<string, string>} */
    let consent = {};
    if (!this.#designMode) {
      try {
        consent = (await privacyApi()).currentVisitorConsent?.() ?? {};
      } catch {
        // No API: nothing ticked
      }
    }

    for (const input of panel.querySelectorAll('[data-okt-consent-purpose]')) {
      if (input instanceof HTMLInputElement) input.checked = consent[input.dataset.oktConsentPurpose ?? ''] === 'yes';
    }

    panel.showDialog();
  };

  #onOpen = () => {
    this.#settle();

    const typing = this.#typing;
    this.#typing = null;
    if (typing && !this.refs.dialog.matches(':modal')) {
      typing.focus({ preventScroll: true });
      return;
    }

    const title = document.getElementById(this.refs.dialog.getAttribute('aria-labelledby') ?? '');
    title?.focus({ preventScroll: true });
  };

  #onPanelOpen = () => {
    const title = document.getElementById(this.#panel?.refs?.dialog?.getAttribute('aria-labelledby') ?? '');
    title?.focus({ preventScroll: true });
  };

  /** The preferences closed without a choice: the banner comes back if it was open */
  #onPanelClose = () => {
    if (!this.#bannerWaits) return;
    this.#bannerWaits = false;
    this.open();
  };

  /** Escape closes the sheet that leaves the page usable only with Close button on (a modal one: Horizon's) */
  #onKeyDown = (/** @type {KeyboardEvent} */ event) => {
    if (event.key !== 'Escape' || this.refs.dialog.matches(':modal') || !this.hasAttribute('data-closable')) return;

    event.preventDefault();
    this.dismiss();
  };

  /** Back to a page the browser kept: the visitor may have answered on another one */
  #onPageShow = (/** @type {PageTransitionEvent} */ event) => {
    if (!event.persisted || !this.refs.dialog.open) return;

    privacyApi()
      .then((api) => {
        if (!this.#isNeeded(api)) this.#close();
      })
      .catch(() => {});
  };

  /** @param {Event} event */
  #isMine(event) {
    return event.target instanceof Element && event.target.contains(this);
  }

  #onSectionSelect = (/** @type {Event} */ event) => {
    if (!this.#isMine(event)) return;
    shownInEditor.set(this.dataset.sectionId, 'banner');
    this.open();
  };

  #onSectionDeselect = (/** @type {Event} */ event) => {
    if (!this.#isMine(event)) return;
    shownInEditor.delete(this.dataset.sectionId);
    this.#bannerWaits = false;
    this.#close();
    if (this.#panel?.refs?.dialog?.open) this.#panel.closeDialog();
  };

  #onBlockSelect = (/** @type {Event} */ event) => {
    const block = event.target;
    if (!(block instanceof Element) || !this.contains(block)) return;

    const panel = this.#panel;
    if (panel?.contains(block)) {
      shownInEditor.set(this.dataset.sectionId, 'preferences');
      this.showPreferences();
      return;
    }

    shownInEditor.set(this.dataset.sectionId, 'banner');
    if (panel?.refs?.dialog?.open) {
      this.#bannerWaits = true;
      panel.closeDialog();
      return;
    }
    this.open();
  };
}

if (!customElements.get('okt-consent')) customElements.define('okt-consent', OktConsent);

/** Opens the cookie preferences: ours while on (or in the theme editor), else Shopify's banner's */
function openPreferences() {
  const ours = document.querySelector('okt-consent[data-enabled], okt-consent[data-design-mode]');
  if (ours instanceof OktConsent) {
    ours.showPreferences();
    return true;
  }

  const banner = /** @type {any} */ (window).privacyBanner;
  if (typeof banner?.showPreferences === 'function') {
    banner.showPreferences();
    return true;
  }

  return false;
}

// Any link to #cookie-preferences, such as the footer menu's "Cookie preferences" (CC5)
document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest(LINKS) : null;
  if (!link) return;

  event.preventDefault();
  openPreferences();
});

// Arriving at an address with #cookie-preferences: the preferences open once the page has loaded (Shopify's banner
// loads its own script, so it may take a moment)
if (location.hash === HASH) {
  history.replaceState(history.state, '', `${location.pathname}${location.search}`);
  loaded.then(async () => {
    for (let attempt = 0; attempt < 20 && !openPreferences(); attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  });
}
